"use client";

import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const categories = ["All", "Kitchen", "Bathroom", "Whole-Home", "Basement", "Outdoor"];
const projects = [{
  id: 1,
  title: "Modern Culinary Kitchen Concept",
  category: "Kitchen",
  description: "Custom rift-cut white oak cabinetry paired with honed Calacatta marble surfaces. Integrated Thermador refrigeration and an expanded architectural island anchor the sunlit space.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_1.png-wvc-sizes",
  alt: "Custom kitchen renovation in Brookhaven featuring white oak cabinetry and honed marble",
  link: "/KitchenRemodelingPage"
}, {
  id: 2,
  title: "Spa-Style Primary Bath Concept",
  category: "Bathroom",
  description: "Freestanding soaking tub framed by full-height hand-mitered zellige tile. Dual unlacquered brass vanities and a zero-entry curbless shower bring spa tranquility home.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_1.png-wvc-sizes",
  alt: "Master bathroom remodel with curbless shower and brass fixtures in Brookhaven",
  link: "/BathroomRemodelingPage"
}, {
  id: 3,
  title: "Open-Concept Whole-Home Concept",
  category: "Whole-Home",
  description: "Complete interior reconfiguration removing load-bearing walls for continuous daylight. European wide-plank oak flooring and custom crown mouldings throughout both floors.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_1.png-wvc-sizes",
  alt: "Whole-home renovation with open-concept living and custom millwork in Brookhaven",
  link: "/WholeHomeInteriorRemodelingPage"
}, {
  id: 4,
  title: "Entertainment Lounge Basement Concept",
  category: "Basement",
  description: "Converted daylight basement with quartz waterfall wet bar and temperature-regulated wine display. Acoustic wall slabbing and flush-mount linear lighting create an intimate lounge.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_project_1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_project_1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_project_1.png-wvc-sizes",
  alt: "Modern basement renovation with custom bar and wine room in Brookhaven",
  link: "/HomeAdditionsPage"
}, {
  id: 5,
  title: "Covered Outdoor Living Veranda Concept",
  category: "Outdoor",
  description: "Covered vaulted cedar porch with masonry gas fireplace and phantom motorized screens. Custom Brazilian ipe decking flows onto stone-bordered landscape patios.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_project_1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_project_1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_project_1.png-wvc-sizes",
  alt: "Outdoor living space and covered porch with masonry fireplace in Brookhaven",
  link: "/HomeAdditionsPage#outdoor-living-decks"
}, {
  id: 6,
  title: "Bespoke Chef's Kitchen & Pantry Concept",
  category: "Kitchen",
  description: "Concealed back-kitchen pantry with soapstone countertops and bespoke reeded glass displays. A 48-inch dual-fuel range and pot filler complete this culinary workspace.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_2.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_2.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_project_2.png-wvc-sizes",
  alt: "Modern chef kitchen remodel with brass accents and custom pantry in Brookhaven",
  link: "/KitchenRemodelingPage"
}, {
  id: 7,
  title: "Architectural Wet Room & Bath Concept",
  category: "Bathroom",
  description: "Fluted walnut double vanity flanked by custom sconces and recessed anti-fog mirrors. Textured limestone slabs line the dual-head wet room and integrated bench seating.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_2.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_2.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_project_2.png-wvc-sizes",
  alt: "Luxury bathroom renovation with limestone wet room in Brookhaven GA",
  link: "/BathroomRemodelingPage"
}, {
  id: 8,
  title: "Modern Classic Whole-Home Concept",
  category: "Whole-Home",
  description: "Floor-to-ceiling iron-and-glass pocket doors connecting kitchen, dining, and courtyard gardens. Tailored architectural built-ins integrate lighting and hidden storage seamlessly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_2.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_2.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_project_2.png-wvc-sizes",
  alt: "Whole home renovation with steel French doors and tailored millwork in Brookhaven",
  link: "/WholeHomeInteriorRemodelingPage"
}];
export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState("All");
  const prefersReduced = useReducedMotion();
  const filteredProjects = activeCategory === "All" ? projects : projects.filter(project => project.category === activeCategory);
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReduced ? 1 : 0,
      y: prefersReduced ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReduced ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="project-gallery" data-nav="light" className="relative bg-background text-foreground py-24 md:py-32 selection:bg-primary/20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial={{
        opacity: prefersReduced ? 1 : 0,
        y: prefersReduced ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: prefersReduced ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">
            Project Gallery
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Our Brookhaven remodeling projects
          </h1>
          <div className="w-16 h-[2px] bg-primary/40 my-4" />
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Explore completed kitchens, baths, and living spaces designed and built for Brookhaven homeowners. Every renovation balances architectural integrity with modern functionality.
          </p>
        </motion.div>

        {/* Concept Disclaimer Note */}
        <motion.p initial={{
        opacity: prefersReduced ? 1 : 0,
        y: prefersReduced ? 0 : 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: prefersReduced ? 0.01 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-xs sm:text-sm text-muted-foreground mb-4">
          These images are concept imagery, not completed Brookhaven Elevation Renovations projects.
        </motion.p>

        {/* Filter Bar */}
        <motion.div initial={{
        opacity: prefersReduced ? 1 : 0,
        y: prefersReduced ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: prefersReduced ? 0.01 : 0.5,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-wrap items-center gap-2 sm:gap-3 mb-10 pb-4 border-b border-border/60">
          {categories.map((category, index) => {
          const isActive = activeCategory === category;
          return <Button key={category} data-index={index} variant={isActive ? "secondary" : "outline"} size="sm" onClick={() => setActiveCategory(category)} className={isActive ? "bg-secondary text-secondary-foreground shadow-xs border-secondary font-medium transition-[background-color,border-color,box-shadow]" : "bg-card text-foreground hover:border-primary/50 hover:bg-muted font-normal transition-[background-color,border-color,box-shadow]"}>
                {category}
              </Button>;
        })}
        </motion.div>

        {/* Projects Grid */}
        <motion.div layout variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, i) => <motion.div key={project.id} data-index={i} layout variants={itemVariants} exit={{
            opacity: 0,
            scale: 0.95,
            transition: {
              duration: 0.2
            }
          }} whileHover={prefersReduced ? {} : {
            y: -4
          }} transition={{
            duration: 0.25,
            ease: [0.2, 0.8, 0.2, 1]
          }} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-250 hover:border-primary/50 hover:shadow-lg">
                {/* 3:2 Aspect Ratio Image Container */}
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={3 / 2}>
                    <img src={project.image} data-wvc-srcset={project.imageSrcset} data-wvc-sizes={project.imageSizes} alt={project.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                  </AspectRatio>
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <Badge variant="secondary" className="backdrop-blur-md bg-secondary/90 text-secondary-foreground text-xs font-medium px-2.5 py-0.5 shadow-xs border-none">
                      {project.category}
                    </Badge>
                  </div>
                </div>

                {/* Card Content */}
                <div className="flex flex-col flex-1 p-5 sm:p-6 justify-between gap-4">
                  <div className="space-y-2">
                    <h3 className="font-serif text-xl sm:text-2xl font-medium tracking-tight text-foreground leading-snug group-hover:text-primary transition-colors duration-200">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-border/40 flex items-center justify-between">
                    <Link to={project.link} className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-200 gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm py-1">
                      <span>Explore details</span>
                      <ArrowUpRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                      Brookhaven, GA
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </motion.div>

        {/* Bottom Estimate Banner */}
        <motion.div initial={{
        opacity: prefersReduced ? 1 : 0,
        y: prefersReduced ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: prefersReduced ? 0.01 : 0.5,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-16 sm:mt-20 p-8 sm:p-10 rounded-2xl border border-border/80 bg-muted/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-xl">
            <h4 className="font-serif text-2xl font-semibold text-foreground">
              Planning your next renovation?
            </h4>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Book a free in-home consultation to review architectural options, local permitting, and realistic timeline projections.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild className="bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-[background-color,box-shadow] shadow-xs">
              <Link to="/ContactRequestAnEstimatePage">
                Request an estimate
              </Link>
            </Button>
            <Button asChild variant="outline" className="bg-card text-foreground font-medium px-5 py-3 rounded-lg hover:bg-accent hover:text-accent-foreground transition-[background-color,border-color]">
              <Link to="/AboutPage">Our 4-step process</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}